export type RequestOutcome<T> = { status: "success"; value: T } | { status: "canceled" } | { status: "failed"; error: unknown };

type ErrorLike = { code?: string; name?: string; response?: { status?: number } };

/** axios 취소와 AbortController 취소를 모두 본다 */
export const isCanceledError = (error: unknown): boolean => {
  const { code, name } = (error ?? {}) as ErrorLike;

  return code === "ERR_CANCELED" || name === "CanceledError" || name === "AbortError";
};

/** 5xx 는 되쏘면 부하가 겹치므로 다시 시도하지 않는다. 상태코드를 모르는 네트워크 장애는 다시 시도한다 */
const isRetryable = (error: unknown): boolean => {
  const status = (error as ErrorLike)?.response?.status;

  return status === undefined || status < 500;
};

/**
 * 요청을 보내고 실패하면 한 번만 다시 시도한다
 * 취소는 재시도하지 않는다. 화면에서 벗어났거나 새 조회가 시작됐다는 뜻이다
 */
export const requestWithRetry = async <T>(request: () => Promise<T>): Promise<RequestOutcome<T>> => {
  try {
    return { status: "success", value: await request() };
  } catch (error) {
    if (isCanceledError(error)) return { status: "canceled" };
    if (!isRetryable(error)) return { status: "failed", error };
  }

  try {
    return { status: "success", value: await request() };
  } catch (error) {
    return isCanceledError(error) ? { status: "canceled" } : { status: "failed", error };
  }
};
